import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as doorsApi from "../../api/doors.api.js";
import { Modal } from "../../components/Modal.js";
import { OutcomeDialog } from "../../components/OutcomeDialog.js";
import { Tile } from "../../components/Tile.js";
import {
  ClockIcon,
  ControllerIcon,
  DoorIcon,
  OverrideIcon,
  RefreshIcon,
  WarningIcon,
} from "../../components/Icon.js";
import { OverrideConfirm } from "../../components/OverrideConfirm.js";
import { useAuth } from "../../lib/auth-context.js";
import { formatAge, formatTimestamp } from "../../lib/format.js";
import { DOORS_POLL_MS, STATUS_POLL_MS, refetchIntervalFor } from "../../lib/polling.js";
import { DoorRow } from "./DoorRow.js";
import type { AuditEntry, CommandOutcome, Door } from "../../types/api.js";

type PendingHold = { door: Door; action: "hold_unlock" | "hold_lock" };

/** Whether a door belongs on this OPERATIONAL screen.
 *
 * The registry is the inventory of every door the service knows about. This screen is the
 * shorter list of doors there is anything to DO about, and the two are deliberately not the
 * same list.
 *
 * A disabled door is one the registry describes as "a controller that has been physically
 * removed": it accepts no commands, the watchdog skips it, and every status field on it is
 * frozen at whatever it last read. Leaving it here puts a row of permanently stale data in the
 * middle of the one screen an operator scans to decide whether the building is secure.
 *
 * THE EXCEPTION IS THE POINT. A disabled door that is still flagged stays visible. Disabling a
 * door does not release an override it is already standing in - the door is physically held,
 * nothing is checking it any more, and hiding it would complete the mistake rather than fix it.
 * The registry's own guidance says the flag is "not to silence one that is misbehaving, because
 * that is the one you want watched"; this is what makes that guidance hold even when someone
 * does it anyway.
 *
 * Exported so that rule is pinned by a test rather than by a comment. */
export function isOperational(door: Door): boolean {
  return door.isEnabled || door.needsAttention || door.overrideActive;
}

export function DoorsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [outcome, setOutcome] = useState<CommandOutcome | null>(null);
  const [pendingHold, setPendingHold] = useState<PendingHold | null>(null);
  const [activityFor, setActivityFor] = useState<Door | null>(null);
  const [busyDoor, setBusyDoor] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const doors = useQuery({
    queryKey: ["doors"],
    queryFn: doorsApi.listDoors,
    refetchInterval: refetchIntervalFor(DOORS_POLL_MS),
  });

  const status = useQuery({
    queryKey: ["status"],
    queryFn: doorsApi.serviceStatus,
    refetchInterval: refetchIntervalFor(STATUS_POLL_MS),
  });

  const activity = useQuery({
    queryKey: ["activity", activityFor?.doorId],
    queryFn: () => doorsApi.doorActivity(activityFor?.doorId ?? "", { limit: 50 }),
    enabled: activityFor !== null,
  });

  const command = useMutation({
    mutationFn: async (run: () => Promise<CommandOutcome>) => run(),
    onMutate: () => setActionError(null),
    onSuccess: (result) => {
      // The API answers 200 whether or not the door moved. This is the branch that matters -
      // never `response.ok`.
      setOutcome(result);
      void queryClient.invalidateQueries({ queryKey: ["doors"] });
      void queryClient.invalidateQueries({ queryKey: ["status"] });
    },
    onError: (error: Error) => setActionError(error.message),
    onSettled: () => setBusyDoor(null),
  });

  function run(door: Door, fn: () => Promise<CommandOutcome>) {
    setBusyDoor(door.doorId);
    command.mutate(fn);
  }

  const list = doors.data ?? [];
  const visible = list.filter(isOperational);
  const hidden = list.length - visible.length;

  const attention = visible.filter((d) => d.needsAttention);
  const actuationEnabled = status.data?.actuationEnabled ?? false;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 id="page-heading" tabIndex={-1}>
            Doors
          </h1>
        </div>
        <div className="row-gap">
          {/* How stale this screen is, on the screen itself.
              `lib/polling.ts` already logs to the CONSOLE when a poller backs off after
              failures - which is no use at all to the person looking at the table. This is the
              one screen where a stuck door appears first, so "is what I am looking at current"
              must be answerable without opening devtools. */}
          <span className="freshness" role="status">
            {doors.isFetching
              ? "updating…"
              : doors.dataUpdatedAt
                ? `updated ${formatAge(new Date(doors.dataUpdatedAt).toISOString())}`
                : "not yet loaded"}
          </span>
          <button
            type="button"
            className="btn"
            onClick={() => void doors.refetch()}
            disabled={doors.isFetching}
          >
            <RefreshIcon aria-hidden="true" focusable="false" />
            {doors.isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <p className="page-intro">
        Every registered door, its confirmed status, and the controls your role allows. This list
        refreshes itself every 10 seconds: a door that drops into an unexpected override appears
        here without anyone pressing anything.
      </p>

      {/* Actuation state. Permanently visible, never dismissible - an operator must never be
          unsure whether a button will move a real door. */}
      <div className={actuationEnabled ? "banner banner-warning" : "banner banner-ok"}>
        <span className="banner-icon" aria-hidden="true">
          {actuationEnabled ? "⚠" : "✓"}
        </span>
        <div className="banner-body">
          {actuationEnabled ? (
            <>
              <strong>Hardware actuation is LIVE.</strong> Open performs a momentary unlock and a
              guaranteed, confirmed return to Normal.
            </>
          ) : (
            <>
              <strong>Hardware actuation is OFF.</strong> Commands are validated and recorded, but
              not sent to any physical door. Status reads still work, so the watchdog can still see
              a stuck door.
            </>
          )}
        </div>
      </div>

      {attention.length > 0 ? (
        <div className="banner banner-critical" role="alert">
          <span className="banner-icon" aria-hidden="true">
            ✗
          </span>
          <div className="banner-body">
            <strong>
              {attention.length} door{attention.length === 1 ? "" : "s"} need
              {attention.length === 1 ? "s" : ""} attention:
            </strong>{" "}
            {attention.map((d) => `${d.name} (${d.currentState})`).join(", ")}. A door in{" "}
            <code>override_suspected</code> may be physically held unlocked.
          </div>
        </div>
      ) : null}

      {doors.isError || doors.failureCount > 0 ? (
        <div className="banner banner-warning" role="alert">
          <span className="banner-icon" aria-hidden="true">
            ⚠
          </span>
          <div className="banner-body">
            <strong>This list is not updating.</strong> The last{" "}
            {doors.failureCount === 1 ? "attempt" : `${doors.failureCount} attempts`} to refresh
            failed, so what you see may be out of date. It keeps retrying. Do not treat a door shown
            here as confirmed until this clears.
          </div>
        </div>
      ) : null}

      {actionError ? (
        <div className="banner banner-critical" role="alert">
          <span className="banner-icon" aria-hidden="true">
            ✗
          </span>
          <div className="banner-body">{actionError}</div>
        </div>
      ) : null}

      <div className="tiles">
        {/* The registered total counts disabled doors, because they ARE registered. The note
            stops that number silently disagreeing with the shorter list underneath it. */}
        <Tile
          label="Doors registered"
          value={String(status.data?.doors.total ?? list.length)}
          note={hidden > 0 ? `${hidden} disabled, not shown below` : undefined}
          icon={<DoorIcon />}
        />
        <Tile
          label="Controllers online"
          value={String(status.data?.doors.online ?? 0)}
          note={`of ${status.data?.doors.total ?? list.length}`}
          icon={<ControllerIcon />}
        />
        <Tile
          label="Overrides active"
          value={String(status.data?.doors.overrideActive ?? 0)}
          tone={(status.data?.doors.overrideActive ?? 0) > 0 ? "warning" : undefined}
          icon={<OverrideIcon />}
        />
        <Tile
          label="Need attention"
          value={String(attention.length)}
          note="unconfirmed or unexpected state"
          tone={attention.length > 0 ? "critical" : undefined}
          icon={<WarningIcon />}
        />
        <Tile
          label="Watchdog"
          value={status.data?.watchdog.running ? "running" : "stopped"}
          small
          note={
            status.data?.watchdog.lastRunAt
              ? `last sweep ${formatTimestamp(status.data.watchdog.lastRunAt)}`
              : "no sweep yet"
          }
          tone={status.data?.watchdog.running ? undefined : "warning"}
          icon={<ClockIcon />}
        />
      </div>

      <div className="panel">
        <div className="table-wrap">
          <table>
            <caption className="visually-hidden">
              Registered doors, their status and the available controls
            </caption>
            <thead>
              <tr>
                <th scope="col">Door</th>
                <th scope="col">Location</th>
                <th scope="col">Status</th>
                <th scope="col">Controller</th>
                <th scope="col">Access mode</th>
                <th scope="col">Override</th>
                <th scope="col">Last action</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((door) => (
                <DoorRow
                  key={door.doorId}
                  door={door}
                  busy={busyDoor}
                  canOpen={can("open_door")}
                  canHold={can("hold_unlock") || can("hold_lock")}
                  canRelease={can("release_override")}
                  canViewAudit={can("view_audit")}
                  onOpen={(d) => run(d, () => doorsApi.openDoor(d.doorId))}
                  onCheck={(d) => run(d, () => doorsApi.statusCheck(d.doorId))}
                  onRelease={(d) => run(d, () => doorsApi.releaseOverride(d.doorId))}
                  onActivity={(d) => setActivityFor(d)}
                  onHold={(d, action) => setPendingHold({ door: d, action })}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Error BEATS empty, and these three states are mutually exclusive. A failed fetch used
            to render "The door list could not be loaded" and "No doors are registered yet."
            one under the other, and on a door console the second sentence is the dangerous one:
            it reads as an all-clear for a list that was never actually received. This is the
            same ordering DataTable enforces for the other screens. */}
        {doors.isLoading ? (
          <div className="loading" role="status">
            Loading doors…
          </div>
        ) : null}
        {!doors.isLoading && doors.isError ? (
          <div className="empty" role="alert">
            The door list could not be loaded. It will retry automatically.
          </div>
        ) : null}
        {/* "Nothing to show" and "everything you have is switched off" are different situations
            and get different sentences. The second one names where to go and fix it. */}
        {!doors.isLoading && !doors.isError && visible.length === 0 ? (
          <div className="empty">
            {list.length === 0
              ? "No doors are registered yet."
              : `All ${list.length} registered doors are disabled. Enable one in the Door registry to control it from here.`}
          </div>
        ) : null}

        {/* Rows are never removed SILENTLY. On a console whose job is telling you what is
            unlocked, a door quietly missing from the list is worse than a door listed as
            irrelevant - the operator has no way to tell "secure" from "not shown". */}
        {!doors.isLoading && !doors.isError && hidden > 0 && visible.length > 0 ? (
          <div className="table-footer">
            <p className="pager-status">
              {hidden} disabled door{hidden === 1 ? " is" : "s are"} hidden — a disabled door
              accepts no commands and the watchdog does not check it.{" "}
              {/* Only offered to a role that can actually open that screen. */}
              {can("manage_doors") ? (
                <>
                  Manage {hidden === 1 ? "it" : "them"} in the{" "}
                  <Link to="/registry">Door registry</Link>.
                </>
              ) : null}
            </p>
          </div>
        ) : null}
      </div>

      {pendingHold ? (
        <OverrideConfirm
          door={pendingHold.door}
          action={pendingHold.action}
          busy={command.isPending}
          onCancel={() => setPendingHold(null)}
          onConfirm={() => {
            const { door, action } = pendingHold;
            setPendingHold(null);
            run(door, () =>
              action === "hold_unlock"
                ? doorsApi.holdUnlock(door.doorId)
                : doorsApi.holdLock(door.doorId),
            );
          }}
        />
      ) : null}

      {outcome ? <OutcomeDialog outcome={outcome} onClose={() => setOutcome(null)} /> : null}

      {activityFor ? (
        <Modal
          title={`Activity — ${activityFor.name}`}
          onClose={() => setActivityFor(null)}
          footer={
            <button type="button" className="btn btn-primary" onClick={() => setActivityFor(null)}>
              Close
            </button>
          }
        >
          {activity.isLoading ? <p>Loading…</p> : null}
          {activity.data && activity.data.items.length === 0 ? (
            <p>Nothing has been recorded for this door yet.</p>
          ) : null}
          {activity.data && activity.data.page.hasMore ? (
            <p className="field-hint">
              Showing the most recent {activity.data.items.length} of{" "}
              {activity.data.page.total.toLocaleString()} entries for this door. The Audit log
              screen can filter and page through all of them.
            </p>
          ) : null}
          <div className="stack">
            {(activity.data?.items ?? []).map((entry: AuditEntry) => (
              <div key={entry.id}>
                <div>
                  <strong>{entry.action}</strong> — {entry.result}
                </div>
                <div className="cell-sub">
                  {formatTimestamp(entry.occurredAt)} · {entry.principalName}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      ) : null}
    </>
  );
}
